import type { PublicSiteId } from '../utils/publicSite'

export interface PublicSiteBranding {
  productDisplayName: string
  loginHeading: string
  navWhySectionLabel: string
  heroTagline: string
  /** Optional supporting line below hero tagline. */
  heroSupportingLine?: string
  /** Optional subtitle / mark line (e.g. feature pillars). */
  productSubtitle?: string
  heroLogoAlt: string
  aboutParagraph: string
  aboutLogoAlt: string
  whySectionHeading: string
  navbarLogoAlt: string
  /** Logo over the hero image (wide marks keep intrinsic aspect ratio). */
  heroMarkLogoSrc?: string
  /** Public hero background image URL; default site uses bundled Storage Walk photo. */
  heroBackgroundImageSrc?: string
  /** Public URL (e.g. `/images/...`) when this site uses a distinct mark; otherwise components fall back to bundled Storage Walk artwork. */
  markLogoSrc?: string
  /** GPS / map marker fill (SVG data URLs cannot use CSS variables). */
  mapMarkerFillHex: string
  /** Tailwind classes for login / reset-password outer `<section>` gradient on `md+`. */
  authPageMdGradientClass: string
  /** Top marketing nav bar background (Tailwind classes). */
  homeNavBarClass: string
  /** Optional CSS class for logo img sizing in nav / sidebar. */
  navLogoClass?: string
  sidebarLogoClass?: string
  /** Contact email for pricing inquiries (shown in PricingPlan popup). */
  pricingContactEmail: string
  /** HTML class name applied to <html> for CSS theming. */
  htmlThemeClass: string
  /** Manifest app name for PWA. */
  manifestName: string
  /** Manifest short name for PWA. */
  manifestShortName: string
  /** Meta description for SEO. */
  metaDescription: string
}

const defaultBranding: PublicSiteBranding = {
  markLogoSrc: undefined,
  mapMarkerFillHex: '#FF5C36',
  authPageMdGradientClass:
    'md:bg-gradient-to-r from-primary via-secondary to-secondary-light',
  homeNavBarClass: 'bg-primary-dark',
  productDisplayName: 'Storage Walk',
  loginHeading: 'Login to Storage Walk',
  navWhySectionLabel: 'Why Storage Walk',
  heroTagline: 'WALK, TRACK, REPORT—YOUR SITE, SECURE',
  heroLogoAlt: 'StorageWalk Logo',
  aboutParagraph:
    'The creation of the Storage Walk App came together out of a need to provide security and transparency for site managers and employees to keep track of daily walks and report issues seen on site. Join the Future of Facility Management. Embrace a solution where technology meets practicality. Whether you own one facility or manage multiple sites, Storage Walk ensures peace of mind through transparency, efficiency, and control.\n\nContact Us: Have questions? Our support team is here to help every step of the way.',
  aboutLogoAlt: 'StorageWalk Logo',
  whySectionHeading: 'Why Storage Walk?',
  navbarLogoAlt: 'Site Walk Logo',
  pricingContactEmail: 'jim@auratecinc.com',
  htmlThemeClass: '',
  manifestName: 'Storage Walk',
  manifestShortName: 'Storage Walk',
  metaDescription: 'Storage Walk Web is a web application for Storage Walk',
}

/** PatrolWalk — Canopy Brand */
const patrolWalkBranding: PublicSiteBranding = {
  markLogoSrc: '/images/patrolwalk-app-white.png',
  heroMarkLogoSrc: '/images/patrolwalk-lockup.png',
  heroBackgroundImageSrc: '/images/patrol-hero.jpg',
  mapMarkerFillHex: '#378ADD',
  authPageMdGradientClass: 'md:bg-primary',
  homeNavBarClass: 'bg-primary',
  navLogoClass: 'h-10 w-auto max-w-[11rem] object-contain',
  sidebarLogoClass: 'h-12 w-auto max-w-[12rem] object-contain',
  productDisplayName: 'PatrolWalk',
  loginHeading: 'Login to PatrolWalk',
  navWhySectionLabel: 'Why PatrolWalk',
  heroTagline: 'Every round. Verified.',
  heroLogoAlt: 'PatrolWalk Logo',
  aboutParagraph:
    'PatrolWalk gives security teams a clear, accountable way to run daily patrols and report what they see on site. See exactly which rounds were completed, when, and by whom—whether you cover one post or many.\n\nContact Us: Have questions? Email hello@patrolwalk.com — our team is here to help.',
  aboutLogoAlt: 'PatrolWalk Logo',
  whySectionHeading: 'Why PatrolWalk?',
  navbarLogoAlt: 'PatrolWalk Logo',
  pricingContactEmail: 'hello@patrolwalk.com',
  htmlThemeClass: 'patrol-walk',
  manifestName: 'PatrolWalk',
  manifestShortName: 'PatrolWalk',
  metaDescription: 'PatrolWalk — AI-powered security patrol accountability. Every round. Verified.',
}

/** ConstructionWalk Brand — cswalk.com */
const constructionWalkBranding: PublicSiteBranding = {
  markLogoSrc: '/images/constructionwalk-header.svg',
  heroMarkLogoSrc: '/images/constructionwalk-lockup.svg',
  heroBackgroundImageSrc: undefined,
  mapMarkerFillHex: '#2F5A3C',
  authPageMdGradientClass: 'md:bg-primary',
  homeNavBarClass: 'bg-primary',
  navLogoClass: 'h-14 w-auto max-w-[14rem] object-contain',
  sidebarLogoClass: 'h-12 w-auto max-w-[12rem] object-contain',
  productDisplayName: 'ConstructionWalk',
  productSubtitle: 'Field Operations · Timekeeping · Daily Job Walks · Sage 100 Contractor & QuickBooks Online',
  loginHeading: 'Login to ConstructionWalk',
  navWhySectionLabel: 'Why ConstructionWalk',
  heroTagline: 'ConstructionWalk documents what happened on the job, not merely who worked there.',
  heroSupportingLine: 'A mobile field operations platform built around the daily construction job walk. Employees, supervisors, project managers and accounting connect directly to Sage 100 Contractor or QuickBooks Online.',
  heroLogoAlt: 'ConstructionWalk Logo',
  aboutParagraph:
    'ConstructionWalk is the digital daily operating record of a construction project.\n\nStorageWalk documents a storage property. PatrolWalk documents a security patrol. ConstructionWalk documents daily construction operations. Same engine: location + assigned person + structured walk + GPS + photos + exceptions. ConstructionWalk adds labor time, job/cost code, daily reports, and accounting sync to Sage 100 Contractor or QuickBooks Online.\n\nContact Us: Have questions? Email hello@sitewalker.org — our team is here to help.',
  aboutLogoAlt: 'ConstructionWalk Logo',
  whySectionHeading: 'Why ConstructionWalk?',
  navbarLogoAlt: 'ConstructionWalk Logo',
  pricingContactEmail: 'hello@sitewalker.org',
  htmlThemeClass: 'construction-walk',
  manifestName: 'ConstructionWalk',
  manifestShortName: 'ConstructionWalk',
  metaDescription: 'ConstructionWalk — The digital daily operating record of a construction project. Timekeeping, daily job walks, Sage 100 Contractor and QuickBooks Online integration.',
}

export function getPublicSiteBranding(siteId: PublicSiteId): PublicSiteBranding {
  switch (siteId) {
    case 'security-walk':
      return patrolWalkBranding
    case 'construction-walk':
      return constructionWalkBranding
    default:
      return defaultBranding
  }
}
